/**
 * Discovery registry for Aave Earn vaults.
 *
 * The Aave GraphQL API cannot enumerate "all vaults on a chain" — it only
 * resolves vaults by explicit `{ address, chainId }` or by curator
 * `ownedBy` owner. So discovery is registry-driven: per chain we pin the
 * curator **owner** addresses whose vaults we surface (the API expands
 * each into that curator's live vault set) plus any one-off vault
 * addresses that don't belong to a tracked curator.
 *
 * Only static discovery metadata lives here — everything per-block
 * (`totalAssets`, `totalSupply`, APR, share price) is fetched live by the
 * API + on-chain enrichment in [`fetchPublic.ts`](./fetchPublic.ts).
 *
 * Extend a chain's `curators` as new Aave Earn curators launch; the
 * fetcher is otherwise chain-agnostic.
 */

/** A curator whose Aave Earn vaults we surface, queried via `ownedBy`. */
export interface AaveEarnCurator {
  /** Vault owner address, lowercased. */
  owner: string
  /** Display name attached to every vault this owner runs. */
  name?: string
}

/** Per-chain discovery config. */
export interface AaveEarnChainRegistry {
  /** Curator owners expanded via the API's `ownedBy` filter. */
  curators: AaveEarnCurator[]
  /** One-off pinned vault addresses (no tracked curator), lowercased. */
  vaults: string[]
}

const EMPTY: AaveEarnChainRegistry = { curators: [], vaults: [] }

/**
 * Per-chain registry keyed by numeric chain id (string).
 *
 * Seeded from the Aave Earn curator set surfaced by the Aave app / API.
 * `owner` addresses are the vaults' on-chain owner (the curator's
 * multisig/EOA), NOT the vault address.
 */
export const AAVE_EARN_REGISTRY: { [chainId: string]: AaveEarnChainRegistry } =
  {
    // Ethereum. Owners run vaults across multiple chains (CREATE3 factory
    // `0xa35995bb2fFC5F2b33379C2e95d00C20FbF71E70`), so the same owner is
    // listed on every chain it deploys to; the fetcher filters `ownedBy`
    // results down to the queried chain.
    '1': {
      curators: [
        // avWETH / avUSDC and 6 more across chains 1/56/42161.
        { owner: '0xfe9BCB07604842cC2F8780B75586F4EF18811668' },
        // avUSDC (Aave USDC Vault Shares).
        { owner: '0x4a7cCbB601d08B0450663b27408e6351727175A4' },
        // Limassol Vault (EURC).
        { owner: '0x888aD9A82a80f0865D872c210191B623edFc5649' },
      ],
      vaults: [],
    },
    // BSC
    '56': {
      curators: [{ owner: '0xfe9BCB07604842cC2F8780B75586F4EF18811668' }],
      vaults: [],
    },
    // Arbitrum
    '42161': {
      curators: [{ owner: '0xfe9BCB07604842cC2F8780B75586F4EF18811668' }],
      vaults: [],
    },
  }

/** Per-chain discovery config; `{ curators: [], vaults: [] }` when unsupported. */
export const getAaveEarnRegistry = (chainId: string): AaveEarnChainRegistry =>
  AAVE_EARN_REGISTRY[String(chainId)] ?? EMPTY

/** True when the chain has at least one curator or pinned vault to query. */
export const hasAaveEarnVaults = (chainId: string): boolean => {
  const reg = getAaveEarnRegistry(chainId)
  return reg.curators.length > 0 || reg.vaults.length > 0
}

/** Resolve a curator display name from a vault owner address. */
export const resolveAaveEarnCuratorName = (
  chainId: string,
  owner: string | null | undefined,
): string | undefined => {
  if (!owner) return undefined
  const target = owner.toLowerCase()
  return getAaveEarnRegistry(chainId).curators.find(
    (c) => c.owner.toLowerCase() === target,
  )?.name
}
